/* eslint-disable max-len */
/* eslint-disable no-console */
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import {pillarRoutes} from '../../esgManager/constants/frameworkRoutes';
import {returnQuestions} from '../../esgManager/constants/returnQuestions';
import {inputType} from '../../esgManager/interface/inputType';
import {
  formatFrameworkFieldValue,
  resolveFrameworkTableCell
} from '../utils/formatFrameworkFieldValue';

import {renderQuestionType, renderValue} from './renderQuestionType';

const hasValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;

  return true;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const frameworkPillarTemplateMapper: Record<string, Record<string, string>> = {
  TCFD: {
    pillar_governance: 'governance',
    pillar_strategy: 'strategy',
    pillar_risk_management: 'riskManagement',
    pillar_metrics_targets: 'matricsAndTargets'
  },
  CDP_SME: {
    pillar_sme_introduction: 'SME Introduction',
    pillar_sme_management: 'SME Identification, Assessment',
    pillar_sme_disclosure: 'SME Disclosure of Risks and Opportunities',
    pillar_sme_governance: 'SME Governance',
    pillar_sme_business_strategy: 'SME Business Strategy',
    pillar_sme_performance_consolidation_approach:
      'SME Environmental Performance - Consolidation Approach',
    pillar_sme_performance_climate_change:
      'SME Environmental Performance - Climate Change',
    pillar_sme_sign_off: 'SME Sign Off'
  },
  CDP_CORPORATE: {
    pillar_corporate_introduction: 'corporateIntroduction',
    pillar_corporate_management:
      'corporateIdentificationAssessmentAndManagementOfDependenciesImpactsRisksAndOpportunities',
    pillar_corporate_disclosure: 'corporateDisclosureofRisksandOpportunities',
    pillar_corporate_governance: 'corporateGovernance',
    pillar_corporate_strategy: 'corporateBusinessStrategy',
    pillar_corporate_performance_consolidation_approach:
      'corporateEnvironmentalPerformanceConsolidationApproach',
    pillar_corporate_performance_climate_change:
      'corporateEnvironmentalPerformanceClimateChange',
    pillar_corporate_environmental_performance_forests:
      'corporateEnvironmentalPerformanceForests',
    pillar_corporate_environmental_performance_water_security:
      'corporateEnvironmentalPerformanceWaterSecurity',
    pillar_corporate_environmental_performance_plastics:
      'corporateEnvironmentalPerformancePlastics',
    pillar_corporate_environmental_performance_biodiversity:
      'corporateEnvironmentalPerformanceBiodiversity',
    pillar_corporate_environmental_performance_financial_services:
      'corporateEnvironmentalPerformanceFinancialServices',
    pillar_corporate_further_information_and_sign_off:
      'corporateFurtherInformationAndSignOff'
  }
};

const isAnswerLeaf = (value: unknown): boolean => {
  if (!isPlainObject(value)) return true;
  if ('value' in value || 'unit' in value) return true;

  return false;
};

const unwrapFrameworkPayload = (raw: unknown): Record<string, any> => {
  if (!raw || typeof raw !== 'object') return {};
  let obj = raw as Record<string, any>;

  // Some APIs return { data: { pillar_*: ... } } (TCFD currently does this),
  // others may double-wrap as { data: { data: {...} } }.
  // Peel a couple of layers without getting stuck in a cycle.
  for (let i = 0; i < 3; i += 1) {
    if (isPlainObject(obj.data)) {
      obj = obj.data as Record<string, any>;
      continue;
    }
    break;
  }

  return obj;
};

/** Report Builder API may return pillars as an array instead of keyed objects. */
export const getFrameworkPillarNodes = (
  raw: Record<string, any> | null | undefined
): any[] => {
  const data = unwrapFrameworkPayload(raw);
  if (!data || typeof data !== 'object') return [];

  const pillarsArr = (data as {pillars?: unknown}).pillars;
  if (Array.isArray(pillarsArr)) {
    return pillarsArr
      .map((p: any) => {
        if (!p || typeof p !== 'object') return null;
        const pid = p.pillarId ?? p.id ?? p.key;
        if (pid == null || String(pid).trim() === '') return null;

        return {...p, pillarId: String(pid)};
      })
      .filter(Boolean);
  }

  return Object.entries(data)
    ?.filter(([key]) => key !== 'framework' && key !== 'data')
    ?.map(([pillarId, pillarValue]) => ({
      pillarId,
      ...(pillarValue ?? {})
    }));
};

const normaliseSasbPillarForTemplate = (rawPillarId: string): string => {
  const compact = String(rawPillarId)
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (compact === 'realestate' || compact === 'pillarrealestate') {
    return pillarRoutes.REAL_ESTATE;
  }

  return rawPillarId;
};

const buildAnswerLookups = (pillar: any) => {
  const byQuestionId: Record<string, any> = {};

  const upsert = (key: string, valueNode: any) => {
    if (!key) return;

    const existing = byQuestionId[key];
    if (existing) {
      const existingValue = existing?.value;
      const mergedValue = Array.isArray(existingValue)
        ? [...existingValue, valueNode]
        : [existingValue, valueNode];
      byQuestionId[key] = {
        ...existing,
        value: mergedValue
      };

      return;
    }

    byQuestionId[key] = {
      questionId: key,
      value: valueNode
    };
  };

  const findAnswer = (node: any) => {
    if (Array.isArray(node)) {
      node.forEach((item) => {
        findAnswer(item);
      });

      return;
    }

    Object.entries(node).forEach(([key, value]) => {
      if (isAnswerLeaf(value)) {
        upsert(key, value);

        if (Array.isArray(value)) {
          findAnswer(value);
        }

        return;
      }

      // Keep object-shaped answers (for example checkbox-group maps like
      // { P1: 'Yes', P2: 'No' }) at their questionId key before diving deeper.
      if (isPlainObject(value) && Object.keys(value).length > 0) {
        upsert(key, value);
      }

      findAnswer(value);
    });
  };

  const collectQuestionNodes = (node: any) => {
    if (node.questionId) byQuestionId[node.questionId] = node;
  };
  findAnswer(pillar);
  collectQuestionNodes(pillar);

  expandAnswerLookupAliases(byQuestionId);

  return byQuestionId;
};

/**
 * Report Builder / ESG payloads may key answers with prefixes, casing, or KPI paths
 * (e.g. `IF_RE_140a_2_water_q8`) while templates use `water_q8`. Register short aliases
 * so mapTemplateQuestionToAnswer can resolve values.
 */
const SASB_REAL_ESTATE_FIELD_KEY =
  /(water_q\d+|tenant_q\d+|energy_q\d+|CC_q\d+)$/i;

const expandAnswerLookupAliases = (byQuestionId: Record<string, any>) => {
  const snapshot = {...byQuestionId};

  for (const [key, entry] of Object.entries(snapshot)) {
    if (!key || entry == null) continue;

    const lower = key.toLowerCase();
    if (lower !== key && byQuestionId[lower] === undefined) {
      byQuestionId[lower] = entry;
    }

    const m = key.match(SASB_REAL_ESTATE_FIELD_KEY);
    if (m?.[1]) {
      const shortKey = m[1].toLowerCase();
      if (byQuestionId[shortKey] === undefined) {
        byQuestionId[shortKey] = entry;
      }
    }
  }
};

const lookupAnswerMatch = (
  byQuestionId: Record<string, any>,
  fieldId: string | undefined
): any => {
  if (fieldId == null || String(fieldId).trim() === '') return undefined;
  const k = String(fieldId);

  return byQuestionId[k] ?? byQuestionId[k.toLowerCase()];
};

/**
 * Input grids store answers per inner fieldName (same as standalone fields).
 * Expand them into template rows so the report builder sidebar matches normal questions.
 */
const inputGridFormFieldsToQuestions = (
  gridField: any,
  item: any,
  headerLabel: string
): any[] => {
  const formFields = gridField?.formContent?.formFields ?? [];

  return formFields
    .filter((ff: any) => ff?.fieldName)
    .map((ff: any) => ({
      questionId: ff.fieldName,
      question: item?.question ?? '',
      parentLabel: headerLabel,
      label: ff?.fieldLabel || ff?.fieldName,
      fieldType: ff?.type ?? ff?.fieldType,
      type: renderQuestionType(ff?.type ?? ff?.fieldType)
    }));
};

/** Expand sidebar field-group rows: INPUT_GRID holds formFields + formCalculations on formContent. */
const flattenFieldGroupEntries = (groupFields: any[]): any[] => {
  const out: any[] = [];

  for (const f of groupFields ?? []) {
    if (!f) continue;
    const ft = f.fieldType ?? f.type;
    if (ft === inputType.INPUT_GRID) {
      const fc = f?.formContent ?? {};
      const formFields = fc.formFields ?? [];
      const formCalculations = fc.formCalculations ?? [];
      out.push(...formFields, ...formCalculations);
    } else {
      out.push(f);
    }
  }

  return out;
};

/** Columns for sidebar preview / insert — ADD_NEW_TO_TABLE and ADD_FIXED_ROW_TO_TABLE. */
const buildSidebarTableColumns = (
  tableField: any
): Array<{key: string; label: string}> => {
  const tableFields = tableField?.formList?.formFields ?? [];
  const dataColumns = tableFields
    .filter((ff: any) => ff?.fieldName)
    .map((ff: any) => ({
      key: String(ff.fieldName),
      label: String(ff.fieldLabel ?? ff.fieldName)
    }));
  const scopeTitle = String(tableField?.scopeColumnTitle ?? '').trim();
  if (scopeTitle) {
    return [{key: 'scopeLabel', label: scopeTitle}, ...dataColumns];
  }

  return dataColumns;
};

/** Heading field types used in tab definitions (static section titles, not answers). */
const HEADING_FIELD_TYPES: readonly string[] = [
  inputType.HEADING,
  inputType.HEADING_2,
  inputType.HEADING_3,
  inputType.HEADING_5
];

const isHeadingFieldType = (ft: any): boolean =>
  HEADING_FIELD_TYPES.includes(ft);

/** Last visible heading text in `fields` before index `end` (e.g. subsection title above a table). */
const lastHeadingTextBeforeIndex = (
  fields: any[],
  endIndex: number
): string => {
  if (!Array.isArray(fields) || endIndex <= 0) return '';

  for (let i = endIndex - 1; i >= 0; i -= 1) {
    const f = fields[i];
    const ft = f?.fieldType ?? f?.type;
    if (!isHeadingFieldType(ft)) continue;

    const text = resolveHeadingDisplayText(f);
    if (text) return text;
  }

  return '';
};

/**
 * Visible heading copy from a field. Some templates mistakenly store an inputType token
 * in `heading`; fall back to fieldLabel when that happens.
 */
const resolveHeadingDisplayText = (field: any): string => {
  const raw = field?.heading;
  const headingAsText =
    typeof raw === 'string' && !HEADING_FIELD_TYPES.includes(raw) ? raw : '';

  return String(headingAsText || field?.fieldLabel || '').trim();
};

/** Slate / sidebar heading level — mirrors field-group mapping in mapTemplateQuestionToAnswer. */
const reportHeadingLevel = (ft: any): 'one' | 'two' | 'three' => {
  if (ft === inputType.HEADING_3) return 'one';
  if (ft === inputType.HEADING_2) return 'two';
  if (ft === inputType.HEADING) return 'three';
  if (ft === inputType.HEADING_5) return 'three';

  return 'one';
};

const standaloneHeadingTemplate = (
  field: any,
  item: any,
  headerLabel: string,
  keyBase: string,
  fieldIdx: number
): any | null => {
  const ft = field?.fieldType ?? field?.type;
  if (!isHeadingFieldType(ft)) return null;

  const headingText = resolveHeadingDisplayText(field);
  if (!headingText) return null;

  const syntheticId =
    field?.fieldName ??
    `heading-${String(keyBase)}-${fieldIdx}-${String(headingText).slice(
      0,
      48
    )}`;

  return {
    questionId: syntheticId,
    question: item?.question ?? '',
    parentLabel: headerLabel,
    label: headingText,
    fieldType: ft,
    type: 'heading-template'
  };
};

const toTemplateQuestions = (subcategory: any): any[] => {
  const items = subcategory?.items ?? [];
  const headerLabel = subcategory?.label ?? '';

  return items?.flatMap((item: any) => {
    const fields = item?.field ?? [];

    const headingTypes = [
      inputType.HEADING,
      inputType.HEADING_2,
      inputType.HEADING_3,
      inputType.HEADING_5
    ];

    const resolveMainHeadingTitle = (mainField: any): string => {
      const headingAsText =
        typeof mainField?.heading === 'string' &&
        !headingTypes.includes(mainField.heading)
          ? mainField.heading
          : '';

      return String(
        headingAsText || mainField?.fieldLabel || item?.question || ''
      ).trim();
    };

    const keyBase =
      subcategory?.kpiId ??
      subcategory?.key ??
      subcategory?.subcategoryId ??
      subcategory?.label ??
      'group';

    // Top-level mainQuestion (non-grid): one sidebar row; keep full `fields` so INPUT_GRID
    // wrappers stay intact for flattening (includes formCalculations).
    const topLevelMainField = Array.isArray(fields)
      ? fields.find((f: any) => f?.mainQuestion)
      : null;

    if (topLevelMainField) {
      const titleText = resolveMainHeadingTitle(topLevelMainField);
      if (!titleText) return [];

      const groupId = `group-${String(keyBase)}-${String(titleText).slice(
        0,
        24
      )}`;

      return [
        {
          questionId: groupId,
          question: titleText,
          parentLabel: headerLabel,
          label: titleText,
          fieldType:
            topLevelMainField?.fieldType ??
            topLevelMainField?.type ??
            inputType.HEADING_3,
          type: 'field-group',
          groupFields: fields
        }
      ];
    }

    // Each INPUT_GRID that declares a nested mainQuestion becomes its own sidebar row so
    // multiple grids in one item (e.g. BRSR Principle 3) all appear. groupFields keeps the
    // full grid field so formCalculations stay attached.
    const nestedMainGrids = Array.isArray(fields)
      ? fields.filter(
          (f: any) =>
            f?.fieldType === inputType.INPUT_GRID &&
            (f?.formContent?.formFields ?? []).some(
              (ff: any) => ff?.mainQuestion
            )
        )
      : [];

    if (nestedMainGrids.length > 0) {
      const groups: any[] = [];

      nestedMainGrids.forEach((mainGridField: any, idx: number) => {
        const nestedMainField = (
          mainGridField?.formContent?.formFields ?? []
        ).find((ff: any) => ff?.mainQuestion);
        const titleText = resolveMainHeadingTitle(nestedMainField);
        if (!titleText) return;

        const groupId = `group-${String(keyBase)}-g${idx}-${String(
          titleText
        ).slice(0, 24)}`;

        groups.push({
          questionId: groupId,
          question: titleText,
          parentLabel: headerLabel,
          label: titleText,
          fieldType:
            mainGridField?.fieldType ??
            nestedMainField?.type ??
            nestedMainField?.fieldType ??
            inputType.HEADING_3,
          type: 'field-group',
          groupFields: [mainGridField]
        });
      });

      if (groups.length > 0) {
        return groups;
      }
    }

    // Item-level mainQuestion (e.g. GRI Foundation): same sidebar behavior as BRSR — one
    // collapsed "main section" row; insert expands to all fields under that item.
    if (item?.mainQuestion && Array.isArray(fields) && fields.length > 0) {
      const titleText = String(item?.question ?? '').trim();
      if (titleText) {
        const keyBase =
          subcategory?.kpiId ??
          subcategory?.key ??
          subcategory?.subcategoryId ??
          subcategory?.label ??
          'group';
        const groupId = `group-${String(keyBase)}-${String(titleText).slice(
          0,
          24
        )}`;

        return [
          {
            questionId: groupId,
            question: titleText,
            parentLabel: headerLabel,
            label: titleText,
            fieldType: inputType.HEADING_3,
            type: 'field-group',
            groupFields: fields
          }
        ];
      }
    }

    const addMoreField = fields.find(
      (field: any) => field?.fieldType === inputType.ADD_MORE_FIELDS
    );
    const addMoreTextAreaField = fields.find(
      (field: any) => field?.fieldType === inputType.ADDMORE_TEXTAREA
    );
    const addNewToTableField = fields.find(
      (field: any) => field?.fieldType === inputType.ADD_NEW_TO_TABLE
    );
    const addFixedRowToTableField = fields.find(
      (field: any) => field?.fieldType === inputType.ADD_FIXED_ROW_TO_TABLE
    );
    const tableTemplateField = addNewToTableField ?? addFixedRowToTableField;

    if (addMoreField || addMoreTextAreaField) {
      const addMoreConfig = addMoreField ?? addMoreTextAreaField;
      const formList = addMoreConfig?.formList ?? {};
      const formFields = formList?.formFields ?? [];
      const siblingQuestions = fields
        .filter((field: any) => {
          return (
            field?.fieldType !== inputType.ADD_MORE_FIELDS &&
            field?.fieldType !== inputType.ADDMORE_TEXTAREA &&
            field?.fieldType !== inputType.ADD_NEW_TO_TABLE &&
            field?.fieldType !== inputType.ADD_FIXED_ROW_TO_TABLE
          );
        })
        .flatMap((field: any, fieldIdx: number) => {
          if (field?.fieldType === inputType.INPUT_GRID) {
            return inputGridFormFieldsToQuestions(field, item, headerLabel);
          }

          const headingTpl = standaloneHeadingTemplate(
            field,
            item,
            headerLabel,
            keyBase,
            fieldIdx
          );
          if (headingTpl) return [headingTpl];

          return [
            {
              questionId: field?.fieldName,
              question: item?.question ?? '',
              label: field?.fieldLabel || field?.fieldName,
              fieldType: field?.fieldType ?? field?.type,
              type: renderQuestionType(field?.fieldType ?? field?.type)
            }
          ];
        });

      const childQuestions = formFields.map((field: any) => ({
        questionId: field?.fieldName,
        question: item?.question ?? headerLabel ?? '',
        parentLabel: headerLabel,
        // Add-more inner fields: do not fall back to fieldName (avoid raw keys in UI)
        label: field?.fieldLabel ?? '',
        fieldType: field?.fieldType ?? field?.type,
        type: renderQuestionType(field?.fieldType ?? field?.type)
      }));

      return [
        ...siblingQuestions,
        {
          questionId: formList?.name,
          question: item?.question ?? headerLabel ?? '',
          parentLabel: headerLabel,
          label:
            addMoreConfig?.fieldLabel ||
            item?.question ||
            formList?.name ||
            headerLabel,
          fieldType: addMoreConfig?.fieldType,
          type: renderQuestionType(addMoreConfig?.fieldType),
          children: childQuestions
        }
      ];
    }

    if (tableTemplateField) {
      const addTableIdx = fields.findIndex(
        (f: any) =>
          f?.fieldType === inputType.ADD_NEW_TO_TABLE ||
          f?.fieldType === inputType.ADD_FIXED_ROW_TO_TABLE
      );
      const headingAboveTable =
        addTableIdx > -1 ? lastHeadingTextBeforeIndex(fields, addTableIdx) : '';

      const fromItem = String(item?.question ?? '').trim();
      const fromFieldLabel = String(
        tableTemplateField?.fieldLabel ?? ''
      ).trim();
      const fromTableControlHeading = resolveHeadingDisplayText(
        tableTemplateField?.controlHeading
      );
      const fromField =
        fromFieldLabel || fromTableControlHeading
          ? fromFieldLabel || fromTableControlHeading
          : '';
      const fromHeader = String(headerLabel ?? '').trim();

      const tableQuestion =
        fromItem || fromField || headingAboveTable || fromHeader || '';
      const columns = buildSidebarTableColumns(tableTemplateField);

      return [
        {
          questionId: tableTemplateField?.fieldName,
          question: `${tableQuestion}`,
          parentLabel: headerLabel,
          label: `${tableQuestion}`,
          fieldType: tableTemplateField?.fieldType,
          type: renderQuestionType(tableTemplateField?.fieldType),
          columns
        }
      ];
    }

    if (fields.length > 1) {
      return fields.flatMap((field: any, fieldIdx: number) => {
        if (field?.fieldType === inputType.INPUT_GRID) {
          return inputGridFormFieldsToQuestions(field, item, headerLabel);
        }

        const headingTpl = standaloneHeadingTemplate(
          field,
          item,
          headerLabel,
          keyBase,
          fieldIdx
        );
        if (headingTpl) return [headingTpl];

        const ft = field?.fieldType ?? field?.type;
        if (
          ft === inputType.ADD_NEW_TO_TABLE ||
          ft === inputType.ADD_FIXED_ROW_TO_TABLE
        ) {
          const tableRowLabel =
            String(field?.fieldLabel ?? '').trim() ||
            resolveHeadingDisplayText(field?.controlHeading) ||
            field?.fieldName;

          return [
            {
              questionId: field?.fieldName,
              question: item?.question ?? '',
              parentLabel: headerLabel,
              label: tableRowLabel,
              fieldType: ft,
              type: renderQuestionType(ft),
              columns: buildSidebarTableColumns(field)
            }
          ];
        }

        return [
          {
            questionId: field?.fieldName,
            question: item?.question ?? '',
            parentLabel: headerLabel,
            label: field?.fieldLabel,
            fieldType: field?.fieldType,
            type: renderQuestionType(field?.fieldType)
          }
        ];
      });
    }

    const firstField = fields[0] ?? {};

    if (firstField?.fieldType === inputType.INPUT_GRID) {
      const fromGrid = inputGridFormFieldsToQuestions(
        firstField,
        item,
        headerLabel
      );

      if (fromGrid.length > 0) {
        return fromGrid;
      }
    }

    const onlyHeading = standaloneHeadingTemplate(
      firstField,
      item,
      headerLabel,
      keyBase,
      0
    );
    if (onlyHeading) return [onlyHeading];

    const ft0 = firstField?.fieldType ?? firstField?.type;
    const tableHeadingForSingleField =
      ft0 === inputType.ADD_NEW_TO_TABLE ||
      ft0 === inputType.ADD_FIXED_ROW_TO_TABLE
        ? resolveHeadingDisplayText(firstField?.controlHeading)
        : '';

    return [
      {
        questionId: firstField?.fieldName,
        question: item?.question ?? '',
        parentLabel: headerLabel,
        label:
          item?.question ??
          firstField?.fieldLabel ??
          (tableHeadingForSingleField || undefined) ??
          firstField?.fieldName,
        fieldType: firstField?.fieldType,
        type: renderQuestionType(firstField?.fieldType)
      }
    ];
  });
};

export const mapTemplateQuestionToAnswer = (
  question: any,
  byQuestionId: Record<string, any>,
  includeUnanswered = false
): any => {
  const matchById = lookupAnswerMatch(byQuestionId, question.questionId);
  let children: any[] = [];

  const {
    questionId,
    label,
    question: questionText,
    type,
    fieldType,
    columns
  } = question;
  const createQuestionNode = (value: any) => {
    return {
      questionId,
      label,
      question: questionText,
      type,
      fieldType,
      columns,
      value: renderValue(value, fieldType)
    };
  };

  if (type === 'heading-template') {
    const text = String(label ?? questionText ?? '').trim();
    if (!text) return null;

    return {
      questionId,
      label,
      question: questionText,
      fieldType,
      type: 'heading-template',
      headingLevel: reportHeadingLevel(fieldType),
      headingText: text
    };
  }

  if (type === 'field-group') {
    const fields = flattenFieldGroupEntries(
      Array.isArray(question?.groupFields) ? question.groupFields : []
    );

    const blocks: any[] = [];
    fields.forEach((f: any) => {
      if (!f) return;
      const ft = f.fieldType ?? f.type;

      if (isHeadingFieldType(ft)) {
        const text = resolveHeadingDisplayText(f);
        if (!text) return;
        blocks.push({
          kind: 'heading',
          level: reportHeadingLevel(ft),
          text
        });

        return;
      }

      const fieldName = f.fieldName;
      if (!fieldName) return;

      const match = lookupAnswerMatch(byQuestionId, fieldName);
      const rawValue = match?.value;
      if (!hasValue(rawValue) && !includeUnanswered) return;

      const answerType = renderQuestionType(ft);
      const renderedValue = renderValue(rawValue, ft);

      if (answerType === 'table') {
        const headers = buildSidebarTableColumns(f);

        const rows = Array.isArray(renderedValue)
          ? renderedValue.map((row: any) => {
              if (Array.isArray(row)) return row;
              if (row && typeof row === 'object') {
                const rec = row as Record<string, unknown>;

                return headers.map((h: any) =>
                  formatFrameworkFieldValue(
                    resolveFrameworkTableCell(rec, String(h.key))
                  )
                );
              }

              return headers.map(() => '');
            })
          : [];

        blocks.push({
          kind: 'qa',
          // No fallback (don't show raw fieldName like P1_QUESTION_91)
          question: String(f.fieldLabel ?? '').trim(),
          answer: {type: 'table', headers, rows}
        });

        return;
      }

      if (!hasValue(rawValue) && includeUnanswered) {
        blocks.push({
          kind: 'qa',
          question: String(f.fieldLabel ?? '').trim(),
          answer: {type: answerType, value: null}
        });

        return;
      }

      blocks.push({
        kind: 'qa',
        // No fallback (don't show raw fieldName keys)
        question: String(f.fieldLabel ?? '').trim(),
        answer: {type: answerType, value: renderedValue}
      });
    });

    if (blocks.length === 0) return null;

    return {
      questionId,
      label,
      question: questionText,
      type,
      fieldType,
      blocks
    };
  }

  if (Array.isArray(question?.children)) {
    if (Array.isArray(matchById?.value)) {
      children = matchById.value.flatMap((row: any) => {
        const rowNode = isPlainObject(row) ? row : {};

        return question.children
          .map((child: any) => {
            const rowValue = (rowNode as Record<string, any>)[child.questionId];

            return {
              questionId: child.questionId,
              label: child.label,
              question: child.question,
              type: child.type,
              fieldType: child.fieldType,
              value: renderValue(rowValue, child.fieldType)
            };
          })
          .filter((child: any) =>
            includeUnanswered ? true : hasRenderableQuestion(child)
          );
      });
    }

    return {...createQuestionNode(matchById?.value), children};
  }

  if (!includeUnanswered && !hasValue(matchById?.value)) {
    return null;
  }

  return createQuestionNode(matchById?.value);
};

const hasRenderableQuestion = (question: any): boolean => {
  if (question?.type === 'heading-template') {
    return Boolean(
      String(question?.headingText ?? question?.label ?? '').trim()
    );
  }

  if (question?.type === 'field-group') {
    const blocks = question?.blocks ?? [];

    return (
      Array.isArray(blocks) &&
      blocks.some((b: any) => b?.kind === 'qa' || b?.kind === 'heading')
    );
  }

  if (Array.isArray(question?.children) && question.children.length > 0) {
    return true;
  }

  return hasValue(question?.value);
};

export interface ProcessFrameworkDataOptions {
  /**
   * When true, include template fields with no saved answer (lookup miss / empty).
   * Report Builder sidebar passes false so only answered fields appear.
   */
  includeUnansweredQuestions?: boolean;
}

export const processFrameworkData = (
  data: any,
  framework: string,
  options?: ProcessFrameworkDataOptions
): any => {
  const includeUnanswered = Boolean(options?.includeUnansweredQuestions);
  const subCategory = useCompanyStore.getState().companyDetails?.subCategory;
  const frameworkId = framework?.toUpperCase();
  const pillarsData = getFrameworkPillarNodes(data);

  return pillarsData
    ?.map((pillar: any) => {
      const rawPillarId = pillar?.pillarId;
      const mappedFromApi =
        frameworkPillarTemplateMapper[String(frameworkId)]?.[
          String(rawPillarId)
        ];
      let templatePillarId = mappedFromApi ?? rawPillarId;
      if (frameworkId === 'SASB') {
        templatePillarId = normaliseSasbPillarForTemplate(
          String(templatePillarId)
        );
      }
      const pillarQuestions = returnQuestions(
        frameworkId,
        templatePillarId,
        subCategory
      );
      if (!pillarQuestions?.tabs?.length) {
        return null;
      }
      const pillarId = frameworkId === 'SASB' ? templatePillarId : rawPillarId;

      const tabs = pillarQuestions?.tabs ?? [];
      const byQuestionId = buildAnswerLookups(pillar);

      const categories = tabs
        .map((tab: any) => {
          const rawSubcategories = tab?.questions ?? [];
          const directQuestions: any[] = [];
          const subcategories =
            (rawSubcategories ?? [])
              .map((subcategory: any) => {
                const questions = toTemplateQuestions(subcategory)
                  ?.map((question: any) => {
                    return mapTemplateQuestionToAnswer(
                      question,
                      byQuestionId,
                      includeUnanswered
                    );
                  })
                  ?.filter((question: any) => {
                    if (question == null) return false;
                    if (includeUnanswered) {
                      if (question.type === 'field-group') {
                        return (question.blocks?.length ?? 0) > 0;
                      }
                      if (question.type === 'heading-template') {
                        return hasRenderableQuestion(question);
                      }

                      return true;
                    }

                    return hasRenderableQuestion(question);
                  });

                if (!questions?.length) return null;

                const label =
                  typeof subcategory?.label === 'string'
                    ? subcategory.label.trim()
                    : '';

                // If API doesn't provide a subcategory label (no accordion level),
                // flatten that hierarchy so the sidebar doesn't render an empty header.
                if (!label) {
                  directQuestions.push(...questions);

                  return null;
                }

                return {
                  subcategoryId:
                    subcategory?.kpiId ?? subcategory?.key ?? tab?.id,
                  label,
                  questions
                };
              })
              .filter(Boolean) ?? [];

          return {
            categoryId: tab?.id,
            label: tab?.title,
            questions: directQuestions,
            subcategories
          };
        })
        .filter((category: any) => {
          const hasSubs =
            Array.isArray(category?.subcategories) &&
            category.subcategories.length > 0;
          const hasDirectQs =
            Array.isArray(category?.questions) && category.questions.length > 0;

          return hasSubs || hasDirectQs;
        });

      return {
        pillarId,
        label: pillarQuestions?.header?.title,
        categories
      };
    })
    .filter(Boolean);
};
